import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Trophy } from "lucide-react";
import { listingHistoryQuery, ownerHistoryQuery } from "@/lib/history-queries";
import { achievementKey, monthSlug, monthYearLabel, type HistoryEntry } from "@/lib/history";
import { useT } from "@/i18n";

function AchievementList({ entries }: { entries: HistoryEntry[] }) {
  const { t, locale } = useT();
  return (
    <ul className="mt-3 space-y-2">
      {entries.map((e) => {
        const badge = achievementKey(e.position);
        return (
          <li key={e.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="rank-numeral text-base tabular-nums">
              #{e.position}
            </span>
            <Link
              to="/history/$year/$month/$board"
              params={{
                year: String(e.year),
                month: monthSlug(e.month),
                board: e.category_slug,
              }}
              search={{ page: undefined }}
              className="font-medium hover:text-primary"
            >
              {e.category_name}
            </Link>
            <span className="text-muted-foreground">
              — {monthYearLabel(e.month, e.year, locale)}
            </span>
            {badge && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                {t(`history.badge.${badge}`)}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * "Historical rankings" block. Renders nothing until there is at least one
 * recorded monthly result, so no page ever shows an empty achievement box.
 */
export function HistoryAchievements({
  listingId,
  ownerUserId,
}: {
  listingId?: string | undefined;
  ownerUserId?: string | undefined;
}) {
  const { t } = useT();
  const listing = useQuery(listingHistoryQuery(listingId));
  const owner = useQuery(ownerHistoryQuery(listingId ? undefined : ownerUserId));
  const entries = (listingId ? listing.data : owner.data) ?? [];
  if (entries.length === 0) return null;

  return (
    <section className="card-surface p-4">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
        <Trophy className="size-4 text-primary" />
        {t("history.achievements")}
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">{t("history.achievements.intro")}</p>
      <AchievementList entries={entries} />
      <Link
        to="/history"
        className="mt-3 inline-block text-xs font-medium text-primary hover:underline"
      >
        {t("history.backToHistory")}
      </Link>
    </section>
  );
}
